import { useEffect, useState } from 'react'
import { AnchoredOverlay } from '@orbind/ui'
import { ExplorerHeaderActions } from '../../shell/explorer.tsx'
import { PainelDeEstilos, type RascunhoDeEstilo } from './MeusEstilos.tsx'
import { TEMAS_DO_VISUAL_NOVO, useVisualNovo } from './visual-novo.ts'

/**
 * Personalizar a Comunicação — a engrenagem ao lado do "+" (pedido 2026-09-14).
 *
 * O usuário quis trocar a cor da parte BRANCA (não o desenho do fundo, que
 * ficou como está): a lista de conversas, o topo da conversa e a barra de
 * escrever. Tons claros e escuros prontos, mais uma cor livre. O desenho do
 * fundo (Orbind / marcado / liso) mudou do topo da conversa para cá — um lugar
 * só para personalizar.
 *
 * É preferência da PESSOA (vai na conta, via layout_prefs): vale em qualquer
 * computador e não passa para o colega.
 */

export const FUNDOS = [
  { id: 'orbind', rotulo: 'Orbind' },
  { id: 'orbind-marcado', rotulo: 'Orbind marcado' },
  { id: 'liso', rotulo: 'Liso' },
  // Papel de parede pronto, claro e escuro, no estilo do mensageiro de
  // referência — não acompanham o tema nem a cor dos painéis (pedido 2026-09-16).
  { id: 'whatsapp-branco', rotulo: 'WhatsApp branco' },
  { id: 'whatsapp-preto', rotulo: 'WhatsApp preto' },
] as const
export type IdDoFundo = (typeof FUNDOS)[number]['id']

/** Cores prontas. `null` = a do tema (branco no claro, azul-escuro no escuro). */
export const CORES_DOS_PAINEIS: readonly { cor: string | null; rotulo: string }[] = [
  { cor: null, rotulo: 'Padrão do tema' },
  { cor: '#EEF3FF', rotulo: 'Gelo' },
  { cor: '#DCE5FF', rotulo: 'Azul claro' },
  { cor: '#F1ECFF', rotulo: 'Lavanda' },
  { cor: '#E6F6EE', rotulo: 'Menta' },
  { cor: '#FBF3E4', rotulo: 'Areia' },
  { cor: '#FCECF0', rotulo: 'Rosé' },
  { cor: '#ECEEF3', rotulo: 'Nuvem' },
  { cor: '#121A3A', rotulo: 'Noite' },
  { cor: '#2A2F3A', rotulo: 'Grafite' },
  { cor: '#153A2D', rotulo: 'Floresta' },
  { cor: '#3A1726', rotulo: 'Vinho' },
]

const HEX = /^#[0-9a-f]{6}$/i

/** Clara ou escura, pela luminância — decide a cor do texto por cima. */
export function tomDaCor(hex: string): 'clara' | 'escura' {
  const canal = (i: number) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  const luminancia = 0.2126 * canal(1) + 0.7152 * canal(3) + 0.0722 * canal(5)
  return luminancia > 0.4 ? 'clara' : 'escura'
}

/**
 * Aplica a cor escolhida enquanto a Comunicação está aberta.
 *
 * A lista de conversas mora no explorer do SHELL, fora da árvore desta tela —
 * por isso a cor vai numa variável da raiz do documento, e o CSS a usa só onde
 * a Comunicação está montada (`.shell:has(.ch-stage)`). Sair da tela limpa.
 */
export function useCorDosPaineis(cor: string | null) {
  useEffect(() => {
    const raiz = document.documentElement
    if (!cor || !HEX.test(cor)) {
      raiz.style.removeProperty('--ch-moldura')
      delete raiz.dataset.chMoldura
      return
    }
    raiz.style.setProperty('--ch-moldura', cor)
    raiz.dataset.chMoldura = tomDaCor(cor)
    return () => {
      raiz.style.removeProperty('--ch-moldura')
      delete raiz.dataset.chMoldura
    }
  }, [cor])
}

/**
 * Cor dos 2 balões de mensagem (SPEC-20260917-0010, "Meus Estilos") — mesma
 * técnica de `useCorDosPaineis`: variável na raiz do documento, limpa ao
 * desmontar.
 */
export function useCoresDosBaloes(minha: string | null, outro: string | null) {
  useEffect(() => {
    const raiz = document.documentElement
    if (minha && HEX.test(minha)) raiz.style.setProperty('--ch-bolha-minha', minha)
    else raiz.style.removeProperty('--ch-bolha-minha')
    if (outro && HEX.test(outro)) raiz.style.setProperty('--ch-bolha-outro', outro)
    else raiz.style.removeProperty('--ch-bolha-outro')
    return () => {
      raiz.style.removeProperty('--ch-bolha-minha')
      raiz.style.removeProperty('--ch-bolha-outro')
    }
  }, [minha, outro])
}

export function PersonalizarComunicacao({
  cor,
  onCor,
  fundo,
  onFundo,
  onPrevia,
}: {
  cor: string | null
  onCor: (cor: string | null) => void
  fundo: IdDoFundo
  onFundo: (fundo: IdDoFundo) => void
  /** Prévia ao vivo do estilo em edição; `null` volta para o estilo ativo. */
  onPrevia: (previa: RascunhoDeEstilo | null) => void
}) {
  const [ancora, setAncora] = useState<HTMLElement | null>(null)
  /** "Meus Estilos" troca o conteúdo DESTE balão — nunca abre outro por cima. */
  const [vendoEstilos, setVendoEstilos] = useState(false)
  const visual = useVisualNovo()
  const livre = cor !== null && !CORES_DOS_PAINEIS.some((c) => c.cor?.toLowerCase() === cor.toLowerCase())

  return (
    <>
      <ExplorerHeaderActions>
        <button
          type="button"
          className="shell__explorer-extra"
          aria-label="Personalizar a Comunicação"
          title="Personalizar cores e fundo"
          aria-haspopup="dialog"
          aria-expanded={ancora !== null}
          onClick={(e) => setAncora(ancora ? null : e.currentTarget)}
        >
          <i className="fas fa-cog" aria-hidden />
        </button>
      </ExplorerHeaderActions>

      {ancora ? (
        <AnchoredOverlay
          anchor={ancora}
          onClose={() => {
            setAncora(null)
            setVendoEstilos(false)
          }}
          prefer="direita"
          role="dialog"
          aria-label="Personalizar a Comunicação"
          className="ch-menu ch-personalizar"
        >
          {vendoEstilos ? (
            <PainelDeEstilos onVoltar={() => setVendoEstilos(false)} onPrevia={onPrevia} />
          ) : (
            <>
          <span className="ch-personalizar__titulo">Personalizar</span>

          {/* Estilo do visual novo (definitivo desde SPEC-20261007-1409). */}
          <span className="ch-personalizar__secao">Estilo</span>
          <div className="ch-vn-temas" role="radiogroup" aria-label="Estilo do visual novo">
            <button
              type="button"
              role="radio"
              aria-checked={visual.tema === null}
              className="ch-vn-tema ch-vn-tema--meus"
              onClick={() => visual.setTema(null)}
            >
              <span aria-hidden />
              Meus Estilos
            </button>
            {TEMAS_DO_VISUAL_NOVO.map((t) => (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={visual.tema === t.id}
                className={`ch-vn-tema ch-vn-tema--${t.id}`}
                onClick={() => visual.setTema(t.id)}
              >
                <span aria-hidden />
                {t.rotulo}
              </button>
            ))}
          </div>

          <span className="ch-personalizar__secao">Cor dos painéis</span>
          <span className="ch-personalizar__dica">Lista de conversas, topo da conversa e barra de escrever.</span>
          <div className="ch-personalizar__cores" role="radiogroup" aria-label="Cor dos painéis">
            {CORES_DOS_PAINEIS.map((opcao) => {
              const marcada = (opcao.cor ?? null)?.toLowerCase() === cor?.toLowerCase() || (opcao.cor === null && cor === null)
              return (
                <button
                  key={opcao.rotulo}
                  type="button"
                  role="radio"
                  aria-checked={marcada}
                  aria-label={opcao.rotulo}
                  title={opcao.rotulo}
                  className={`ch-personalizar__cor${marcada ? ' ch-personalizar__cor--marcada' : ''}${
                    opcao.cor === null ? ' ch-personalizar__cor--tema' : ''
                  }`}
                  style={opcao.cor ? { background: opcao.cor } : undefined}
                  onClick={() => onCor(opcao.cor)}
                >
                  {marcada ? (
                    <i
                      className="fas fa-check"
                      aria-hidden
                      style={{ color: opcao.cor && tomDaCor(opcao.cor) === 'escura' ? '#fff' : undefined }}
                    />
                  ) : null}
                </button>
              )
            })}
            {/* Cor livre: o seletor de cor do sistema. */}
            <label
              className={`ch-personalizar__cor ch-personalizar__cor--livre${livre ? ' ch-personalizar__cor--marcada' : ''}`}
              title="Escolher outra cor"
              style={livre ? { background: cor! } : undefined}
            >
              <input
                type="color"
                aria-label="Escolher outra cor"
                value={cor ?? '#eef3ff'}
                onChange={(e) => onCor(e.target.value)}
              />
              <i
                className={livre ? 'fas fa-check' : 'fas fa-eye-dropper'}
                aria-hidden
                style={{ color: livre && tomDaCor(cor!) === 'escura' ? '#fff' : undefined }}
              />
            </label>
          </div>

          {/* Combinações nomeadas: a configuração da aparência mora aqui, dentro
              da Comunicação (correção do usuário, 2026-09-17). */}
          <button type="button" className="ch-personalizar__estilos" onClick={() => setVendoEstilos(true)}>
            <i className="fas fa-palette" aria-hidden />
            <span>
              Meus Estilos
              <small>Salve combinações com nome e troque quando quiser.</small>
            </span>
            <i className="fas fa-chevron-right" aria-hidden />
          </button>

          <span className="ch-personalizar__secao">Desenho no fundo da conversa</span>
          <div className="ch-personalizar__fundos" role="radiogroup" aria-label="Desenho no fundo da conversa">
            {FUNDOS.map((opcao) => (
              <button
                key={opcao.id}
                type="button"
                role="radio"
                aria-checked={fundo === opcao.id}
                className={`ch-personalizar__fundo${fundo === opcao.id ? ' ch-personalizar__fundo--marcado' : ''}`}
                onClick={() => onFundo(opcao.id)}
              >
                <span className={`ch-fundo__amostra ch-fundo ch-fundo--${opcao.id}`} aria-hidden />
                {opcao.rotulo}
              </button>
            ))}
          </div>
            </>
          )}
        </AnchoredOverlay>
      ) : null}
    </>
  )
}
